'use client';

import { useState } from 'react';
import ComboBox from '@pixum/combobox';
import { LeadingType } from '@pixum/menu-item';
import type { CreditCardType } from '../../../../types';
import { getCreditCardIcon } from '../../../../../utils/payment/getCreditCardIcon';
import { getCreditCardLabelByCardType } from '../../../../../utils/payment/getCreditCardLabelByCardType';
import styles from './CreditCardPaymentMethod.module.css';

interface CreditCardPaymentMethodProps {
  types: CreditCardType[];
  value: CreditCardType;
  onChange: (type: CreditCardType) => void;
  constants: Record<string, string>;
}

export function CreditCardPaymentMethod({ types, value, onChange, constants }: CreditCardPaymentMethodProps) {
  const [isOpen, setIsOpen] = useState(false);

  const options = types.map(type => ({
    key: type,
    text: getCreditCardLabelByCardType(type, constants),
    leadingProps: { type: LeadingType.Icon, component: getCreditCardIcon(type) }
  }));

  // ComboBox reports the option text, so map the label back to its PSI type.
  const handleOptionClick = (label: string) => {
    const selectedCardType = types.find(type => getCreditCardLabelByCardType(type, constants) === label);

    if (selectedCardType) {
      onChange(selectedCardType);
    }

    setIsOpen(false);
  };

  return (
    <ComboBox
      rootClassName={styles['creditCardCombobox']}
      label={constants['i18n_credit_card_label']}
      headline={constants['i18n_credit_card_label']}
      options={options}
      value={getCreditCardLabelByCardType(value, constants)}
      leadingIcon={getCreditCardIcon(value)}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onClick={() => setIsOpen(open => !open)}
      onOutsideClick={() => setIsOpen(false)}
      onOptionItemClick={handleOptionClick}
      withSearch={false}
      openOnEnter={false}
    />
  );
}
